import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UilStopwatch } from '@iconscout/react-unicons';
import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';
import RevealDropdown, {
  RevealDropdownTrigger
} from '../../components/base/RevealDropdown';
import ProjectDetailsSummary from '../../components/project-management/project-details/ProjectDetailsSummary';
import CorrectTokenLengthModal from '../../components/modals/CorrectTokenLengthModal';
import AddPhaseDialog from '../../components/production/AddPhaseDialog';
// The same form Execution History opens on a row. Reached from here too, because
// this is the page somebody is on when they notice a drum is wrong — and the
// drum's own specification (planned length, what it is wound on, where it goes)
// now reaches the job from it. See redis_dev/helpers/drumFields.js.
import EditExecutionLogModal from '../../components/modals/EditExecutionLogModal';
import { fetchJobExecutionLogs } from '../../features/execution-logs/api/executionLogsApi';
import { http } from '../../features/jobs/api/http';
import { useQuery } from '@tanstack/react-query';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useMainLayoutContext } from '../../providers/MainLayoutProvider';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Col, Dropdown, Modal, ProgressBar, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TaskCompletedGant from '../../components/project-management/project-details/TaskCompletedGant';
import WorkLoads from '../../components/project-management/project-details/WorkLoads';
import ConsumptionDetails from './ConsumptionDetails';
import TeamMembers from '../../components/project-management/project-details/TeamMembers';
import { useParams } from 'react-router-dom';
import { useJob } from '../../features/jobs/hooks/useJob';
import { Bars } from 'react-loader-spinner';
import { useQueryClient } from '@tanstack/react-query';
import { detachJobFromOrder } from '../../features/jobs/api/jobsApi';
import { getJobStatusUI } from '../../helpers/jobStatus';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import FilterTab from '../../components/common/FilterTab';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import { PHASE_LABEL, PHASES_ORDER } from './ProjectJobsMonitor';
import { useTokenEstimates } from '../../features/machine-intervals/hooks/useMachineIntervals.js';
import { projectRowsToEstimateTokens } from '../../features/machine-intervals/api/machineIntervalsApi.js';
import { EstimateCell, fmtEta } from '../../components/machines/EstimateCell.js';
import { API } from '../../config/api';
import {
  useJobOrdersSummary,
  useOrders
} from '../../features/orders/useOrders';
import EntityEventTimeline from '../../components/timelines/EntityEventTimeline';
import { useJobExecutionLogs } from '../../features/execution-logs/hooks/useExecutionLogs';
import { operatorsByTask } from '../../helpers/taskOperators';
// Which physical bobbin a token is about. Metres drawn off somebody else's drum
// get a token per consuming product, and the operator at the panel sees the
// DRUM — so this page has to name the same thing.
import { drumOfToken } from '../../helpers/sharedTokens';
import {
  machineTraceAvailability,
  isSimulatedOrigin
} from '../../helpers/tokenTrace';

// Phases that can be re-edited in the wizard: any phasePlan phase with at least
// one PENDING, not-yet-produced drum (in phasePlan/dependency order).
function getEditablePhases(job) {
  const phasePlan = job?.phasePlan || [];
  const tokens = job?.tokens || {};
  const isPendingTok = t =>
    String(t?.status || '').toUpperCase() === 'PENDING' &&
    Number(t?.producedLength || 0) === 0;
  const out = [];
  for (const p of phasePlan) {
    const hasPending = Object.values(tokens).some(
      t => String(t?.phase || '') === p.name && isPendingTok(t)
    );
    if (hasPending) out.push(p.name);
  }
  return out;
}

// A product launched as part of a MERGE (shared drum) or GROUP-BY (color / wire-Ø
// ordering) must be edited together with its group from the machine queue — editing
// it standalone here would break the merge / lose the grouping. Both are stamped on
// the tokens (mergeQueueKey / groupByKey), so detect either.
function isGroupedProduct(job) {
  const tokens = job?.tokens || {};
  return Object.values(tokens).some(t => t?.mergeQueueKey || t?.groupByKey);
}

const ProjectDetails = () => {
  const { setContentClass } = useMainLayoutContext();
  const [showTaskTable, setShowTaskTable] = useState(false);
  const [showOrdersTable, setShowOrdersTable] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Adding a phase from HERE, not only at warehouse reception.
  //
  // The 3-dot menu on Production -> Warehouse could always do it, but that is
  // the end of the line: you had to wait for the drum to reach reception before
  // you could say "actually this one needs coiling". This is the page somebody
  // is on when they realise it, so the same dialog opens here too — product
  // level, no tokenId, which reroutes every drum of the job at that phase.
  const [addPhaseTarget, setAddPhaseTarget] = useState(null);
  const [addPhaseFlash, setAddPhaseFlash] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDeleteJob = async () => {
    setDeleting(true);
    try {
      const http = axios.create({ baseURL: API.REDIS, timeout: 10000 });
      http.interceptors.request.use(cfg => {
        const token = localStorage.getItem('token');
        if (token) cfg.headers['x-auth-token'] = token;
        return cfg;
      });
      await http.delete(`/api/redis/job/${id}`);
      navigate('/apps/project-management/project-list-view');
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
    setDeleting(false);
  };

  const { id } = useParams();
  const { data: job, isLoading, isError, error } = useJob(id);
  useEffect(() => {
    setContentClass('px-0 pt-navbar');

    return () => {
      setContentClass('');
    };
  }, []);

  if (isLoading) {
    return (
      <Bars
        height="80"
        width="80"
        radius="9"
        color={'#3874ff'}
        ariaLabel="three-dots-loading"
        wrapperStyle={styles.spinnerContainer}
      />
    );
  }

  if (!job) {
    return (
      <div className="text-center py-6">
        <h4 className="text-700">Job not found</h4>
        <p className="text-500">The job may have been removed or the ID is invalid.</p>
        <Link to="/apps/project-management/project-list-view" className="btn btn-primary btn-sm">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <Row className="g-0">
      <Col xs={12} xxl={12} className="px-0 bg-soft">
        <div className="px-4 px-lg-6 pt-6 pb-9">
          <div className="mb-5">
            <div className="d-flex justify-content-between">
              <h2 className="text-black fw-bolder mb-2">Project overview</h2>
              <RevealDropdownTrigger>
                <RevealDropdown>
                  {(() => {
                    // Merge / group-by products: block standalone edit — must be
                    // edited together from the machine queue.
                    if (isGroupedProduct(job)) {
                      return (
                        <Dropdown.Item
                          disabled
                          title="This product is merged / grouped — edit it together with its group from the machine queue"
                        >
                          Edit — please edit from machine queue
                        </Dropdown.Item>
                      );
                    }
                    const editable = getEditablePhases(job);
                    if (!editable.length) {
                      return <Dropdown.Item disabled>Edit (no pending phase)</Dropdown.Item>;
                    }
                    return editable.map(ph => (
                      <Dropdown.Item
                        key={ph}
                        onClick={() =>
                          navigate('/apps/project-management/edit-phase-wizard', {
                            state: { jobId: job.jobId, phase: ph }
                          })
                        }
                      >
                        Edit {ph.replaceAll('_', ' ')}
                      </Dropdown.Item>
                    ));
                  })()}
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() =>
                      setAddPhaseTarget({
                        jobId: job.jobId,
                        cableName: job.productLabel || job.name || null
                      })
                    }
                    title="Send this product on to coiling, rewinding or an outer sheath — the same thing the warehouse reception menu does, without waiting for it to get there"
                  >
                    Add phase…
                  </Dropdown.Item>
                  <Dropdown.Item className="text-danger" onClick={() => setShowDeleteConfirm(true)}>Delete</Dropdown.Item>
                </RevealDropdown>
              </RevealDropdownTrigger>
            </div>
            <Badge
              variant="phoenix"
              bg={getJobStatusUI(job.status).type}
              iconPosition="end"
              icon={<UilStopwatch size={12} className="ms-1" />}
            >
              {getJobStatusUI(job.status).label}
            </Badge>

            {/* The outcome of an add-phase, in words. The job has reopened and
                the drums have moved between two services; "nothing is scheduled
                yet" is the part that otherwise gets missed. */}
            {addPhaseFlash && (
              <Alert
                variant={
                  addPhaseFlash.variant === 'warning' ? 'phoenix-warning' : 'phoenix-success'
                }
                className="py-2 fs-9 mt-3 mb-0"
                dismissible
                onClose={() => setAddPhaseFlash(null)}
              >
                {addPhaseFlash.message}
              </Alert>
            )}
          </div>
          <Row className="gx-0 gx-sm-5 gy-8 mb-8">
            <Col xs={12} xl={3} xxl={4} className="pe-xl-0">
              <ProjectDetailsSummary
                project={job}
                showTaskTable={showTaskTable}
                setShowTaskTable={setShowTaskTable}
                showOrdersTable={showOrdersTable}
                setShowOrdersTable={setShowOrdersTable}
              />
            </Col>

            <Col xs={12} xl={9} xxl={8}>
              <TaskCompletedGant job={job} />
            </Col>
            <Col
              xs={12}
              xxl={12}
              // className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 border-top border-right border-300"
            >
              {/* The two sit in the same column, so with both open they ran
                  straight into each other — the gap only appeared when one was
                  closed. The margin goes on the FIRST one and only while the
                  second is actually showing, so a single open table keeps the
                  spacing it had. */}
              <Col xs={12} xxl={12} className={showTaskTable && showOrdersTable ? 'mb-6' : ''}>
                {showTaskTable && <ProjectTasksTable project={job} />}
              </Col>

              <Col xs={12} xxl={12}>
                {showOrdersTable && <ProjectOrdersTable project={job} />}
              </Col>
            </Col>
            <Col xs={12} sm={7} lg={9} xl={6}>
              <ConsumptionDetails
                title="Material plan (simulation)"
                subtitle="From materialPlanSummary"
                summary={job.materialPlanSummary || []}
              />
            </Col>
            <Col xs={12} sm={7} lg={9} xl={6}>
              <ConsumptionDetails
                title="Material execution (actual)"
                subtitle="From materialExecutionSummary"
                summary={job.materialExecutionSummary || []}
              />
            </Col>
          </Row>
        </div>
      </Col>
      {/* This was a bare <div> sitting directly inside the Row, outside the
          `px-4 px-lg-6` wrapper that gives the rest of the page its margins. A
          Row carries a negative horizontal margin for its gutters and expects
          its children to be Cols that put the padding back; a plain div puts
          nothing back, so the block hung outside the page on both sides, and
          `mx-lg-n4` pulled it a further 1.5rem out again.
          On the left that had been papered over with a hand-set 30px margin on
          the first card, which is why only the right-hand side ran under the
          navigation. Both hacks are gone; it is a Col with the page's own
          padding now, like everything above it. */}
      {/* There were two more cards here — an "Activity" and a "Report" timeline
          reading job.activity[] and job.reports[] out of the Redis blob. Full
          history's own Decisions and Production tabs are the same two feeds, so
          the page showed each event three times.

          Checked before removing them rather than assumed: across 23 live jobs,
          all 29 activity entries and all 123 reports were present in MongoDB.
          The one thing lost: those cards rendered while MongoDB was down, and
          this one deliberately refuses to. */}
      <Col xs={12} className="px-4 px-lg-6 mb-3">
        <EntityEventTimeline
          jobId={job?.jobId || id}
          title="Full history"
          subtitle="Every event recorded against this product — launches, edits, forks, corrections, production."
        />
      </Col>

      {/* The same dialog the warehouse reception menu opens — one definition, so
          "the same thing as the table does" stays true. */}
      <AddPhaseDialog
        target={addPhaseTarget}
        onHide={() => setAddPhaseTarget(null)}
        onDone={res => setAddPhaseFlash(res)}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton className="bg-danger bg-opacity-10">
          <Modal.Title className="text-danger">Delete Job</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this job?</p>
          <p className="fw-bold">{job?.productLabel} — {job?.jobCode}</p>
          <div className="alert alert-warning py-2 small">
            This will:
            <ul className="mb-0 mt-1">
              <li>Archive the job to MongoDB for traceability</li>
              <li>Reverse all order allocations</li>
              <li>Remove tasks from machine queues</li>
              <li>Clean up phantom tokens</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteJob} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Job'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

const styles = {
  spinnerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Add a background with some opacity
    zIndex: 1000 // Ensure the spinner is on top
    // backgroundColor: '#000', // Optional: background color
  }
};

export default ProjectDetails;
function normalizeStatus(status) {
  return String(status || 'PENDING').toUpperCase();
}

function isDoneStatusUI(status) {
  const s = normalizeStatus(status);
  return s === 'DONE' || s === 'FINISHED' || s === 'COMPLETED';
}

function progressToPercent(p) {
  if (p == null) return 0;
  const n = Number(p);
  if (!Number.isFinite(n)) return 0;
  if (n > 0 && n <= 1) return Math.round(n * 100);
  return Math.round(n);
}

function statusBadge(status, progress) {
  const s = normalizeStatus(status);
  const pct = progressToPercent(progress);
  const done = isDoneStatusUI(s) || pct >= 100;

  if (done) return { label: 'DONE', type: 'success' };

  if (s === 'PENDING') return { label: 'PENDING', type: 'warning' };
  if (s === 'ACTIVE' || s === 'RUNNING' || s === 'IN_PROGRESS')
    return { label: s, type: 'info' };
  if (s === 'INACTIVE' || s === 'PAUSED')
    return { label: s, type: 'secondary' };
  if (s === 'ERROR' || s === 'FAILED') return { label: s, type: 'danger' };

  return { label: s, type: 'secondary' };
}

function phaseIndex(phase) {
  const idx = PHASES_ORDER.indexOf(phase);
  return idx === -1 ? 999 : idx;
}

function hashToHue(str) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

function normalizeTokenColor(color) {
  const c = String(color || '')
    .trim()
    .toUpperCase();
  if (!c) return null;

  if (c.startsWith('#')) return c;

  const MAP = {
    BLUE: '#1e6bd6',
    BROWN: '#7a4a2f',
    'GREEN-YELLOW': '#b6d400',
    GREEN: '#2fb344',
    YELLOW: '#f2c037',
    BLACK: '#1f2328',
    WHITE: '#f8f9fa',
    GREY: '#adb5bd',
    GRAY: '#adb5bd',
    RED: '#d63939',
    ORANGE: '#f76707',
    NATUR: '#c8c8c8'
  };

  return MAP[c] || '#6c757d';
}

// -------------------- progress ui --------------------
function progressVariant(pct) {
  if (pct < 20) return 'danger';
  if (pct < 60) return 'warning';
  if (pct < 90) return 'info';
  return 'success';
}

function ProgressCell({ pct }) {
  const value = Math.max(0, Math.min(100, Number(pct || 0)));
  const variant = progressVariant(value);

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 140 }}>
      <div className="flex-grow-1">
        <ProgressBar
          now={value}
          variant={variant}
          style={{ height: 10, borderRadius: 999 }}
        />
      </div>
      <span
        className="fs-9 text-700"
        style={{ minWidth: 42, textAlign: 'right' }}
      >
        {value}%
      </span>
    </div>
  );
}

// -------------------- grouping row styling --------------------
function cellPropsForRow(row, extraClass = '') {
  const o = row?.original || {};
  const isContinue = !!o.groupContinue;

  const bg =
    o.groupIndex % 2 === 0
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(255,255,255,0.06)';

  const accent = `hsl(${o.groupHue || 210} 70% 55%)`;

  const style = {
    background: bg,
    ...(isContinue ? { borderTop: '0px solid transparent' } : {}),
    borderLeft: `4px solid ${accent}`
  };

  const basePy = isContinue ? 'py-2' : 'py-3';

  return {
    className: `white-space-nowrap ${basePy} ${extraClass}`.trim(),
    style
  };
}

// -------------------- chips pentru tokenIds (click + dot color) --------------------
/**
 * One physical bobbin under more than one product, on the drum chip.
 *
 * The same badge Project Token State shows (`⇄ N products`, warning), plus the
 * partners as links — a planner who sees a drum is shared immediately wants to
 * know by whom. NEW TAB on purpose: this page is where somebody is reading a
 * product, and navigating away to answer a side question loses their place.
 */
const SharedDrumChip = ({ partners }) => {
  const named = (partners || []).filter(p => p?.jobId);
  if (!named.length) return null;

  return (
    <>
      <Badge
        bg="warning"
        style={{ fontSize: 8 }}
        className="ms-1 align-middle"
        title={`One physical bobbin, shared with: ${named
          .map(p => p.productLabel || p.jobId)
          .join(', ')}. The same drum is listed under each of them — do not count it twice.`}
      >
        ⇄ {named.length + 1} products
      </Badge>
      <span className="ms-1 fs-10">
        {named.map((p, i) => (
          <span key={p.jobId}>
            {i > 0 && ', '}
            <a
              href={`/apps/project-management/project-details/${p.jobId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-warning"
              title={`Open ${p.productLabel || p.jobId} in a new tab`}
            >
              {p.productLabel || String(p.jobId).slice(-6)}
            </a>
          </span>
        ))}
      </span>
    </>
  );
};

function TokenChips({
  ids,
  tokenMetaById,
  onTokenClick,
  logByTokenId,
  onEditLog,
  sharedByToken = {}
}) {
  const arr = Array.isArray(ids) ? ids : [];
  if (!arr.length) return <span className="text-600">—</span>;

  return (
    <div className="d-flex flex-wrap gap-2">
      {arr.map(id => {
        const meta = tokenMetaById?.[id] || {};
        const dotColor = meta?.color ? normalizeTokenColor(meta.color) : null;
        // THE BOBBIN, not the token. Metres drawn off somebody else's drum get
        // a token per consuming product, so one reel read as several ids — and
        // the operator at the panel sees the drum, so this page has to agree
        // with it (2026-11-05). Falls back to the token for every other row.
        const drumId = drumOfToken(meta, id) || id;

        return (
          <span
            key={id}
            role="button"
            tabIndex={0}
            onClick={() => onTokenClick?.(id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onTokenClick?.(id);
            }}
            className="px-2 py-1 rounded-2 d-inline-flex align-items-center gap-2"
            style={{
              fontFamily: 'monospace',
              fontSize: 12,
              background: 'rgba(0,0,0,0.06)',
              userSelect: 'none',
              cursor: 'pointer'
            }}
            title="Click for details"
          >
            <span title={drumId === id ? id : `${drumId} — this product's share is ${id}`}>
              {drumId}
            </span>

            {/* This drum, editable from here.
                Where a production record exists the form edits both it and the
                drum's specification. Where one does not — a drum queued or still
                winding, since a record is written when a drum FINISHES — it
                opens on the SPECIFICATION alone, which is the half that can
                still change anything: the planned length is what tells the
                machine where to stop. It used to be offered only with a record,
                which left the running drum as the one thing that could not be
                corrected (2026-11-02). */}
            {(() => {
              const log = logByTokenId?.[id];
              if (!log || !onEditLog) return null;
              return (
                <span
                  role="button"
                  tabIndex={0}
                  title={
                    log._id
                      ? 'Edit this drum and its execution record'
                      : 'Edit this drum — planned length, reel, delivery. No record yet: it is queued or still winding.'
                  }
                  onClick={e => {
                    e.stopPropagation();
                    onEditLog(log);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onEditLog(log);
                    }
                  }}
                  className="text-primary"
                  style={{ cursor: 'pointer', lineHeight: 1 }}
                >
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: 10 }} />
                </span>
              );
            })()}

            {dotColor && (
              <span
                title={String(meta.color)}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: dotColor,
                  display: 'inline-block',
                  border: '1px solid rgba(0,0,0,0.25)'
                }}
              />
            )}

            {/* A phase of this product that ran together with another one. */}
            <SharedDrumChip partners={sharedByToken?.[id]} />
          </span>
        );
      })}
    </div>
  );
}

// -------------------- mapping tasks -> rows --------------------
function toTaskRows(project) {
  const tasksObj = project?.tasks || {};
  const tokensObj = project?.tokens || {};
  const jobId = project?.jobId || project?._id || project?.id || '';

  const rows = [];
  for (const t of Object.values(tasksObj)) {
    const inputs = Array.isArray(t.inputs) ? t.inputs : [];
    const outputs = Array.isArray(t.outputs) ? t.outputs : [];
    const groupKey = `${jobId}::${t.phase || ''}`;
    const groupHue = hashToHue(groupKey);

    // Wire spec (Ø) for this task — from the COPPER entry of its materialPlan.
    // Per-task: drums in the same job can carry different wire diameters.
    const copperMp = (Array.isArray(t.materialPlan) ? t.materialPlan : []).find(
      m => String(m?.material || '').toUpperCase() === 'COPPER'
    );
    const wireDiameter =
      copperMp && copperMp.wireDiameter != null ? Number(copperMp.wireDiameter) : null;
    const wireType = copperMp ? String(copperMp.wireType || '').toUpperCase() : null;

    // One row per OUTPUT TOKEN (drum): a task split into several bobbins
    // (e.g. exterior_sheat 3500/3500/3000, armouring, …) is ONE task with many
    // output tokens — show each drum as its own execution instead of collapsing
    // them into a single row. Tasks with no outputs fall back to one task row.
    const outIds = outputs.length ? outputs : [null];
    for (const outId of outIds) {
      const tok = outId ? tokensObj[outId] : null;

      // per-drum values when the token exists; otherwise task-level values
      const planned = tok
        ? Number(tok.plannedLength ?? 0) || 0
        : Number(t.plannedLength ?? 0) || 0;
      const produced = tok
        ? Number(tok.producedLength ?? 0) || 0
        : Number(t.executedLength ?? 0) || 0;
      const tokStatus = tok ? tok.status : t.status;
      const tokProgress = tok ? tok.progress : t.progress;

      rows.push({
        taskId: t.taskId,
        rowKey: outId ? `${t.taskId}::${outId}` : t.taskId,
        phase: t.phase || '',
        tandem: !!t.tandem,
        tandemPhases: Array.isArray(t.tandemPhases) ? t.tandemPhases : null,
        machineId: t.machineId || '',
        // Phantom-sourced tasks (pre-made stock, incl. COPPER_ROPE consumed as
        // pre_stranding) run on no machine of this job — show PHANTOM, not a dash.
        isPhantomSource: !!t.isPhantomSource,
        // Factory Sync backfills production that happened before the system was
        // recording it, so there is genuinely no machine to name. A dash reads as
        // missing data; this says where the row came from.
        isFactorySync:
          t.completedReason === 'factory_sync' || tok?.completedReason === 'factory_sync',
        // Completed from a desk rather than by the line. The machine is real
        // and worth keeping on screen — it is where the drum WOULD have run —
        // but nothing on it was measured, which is the part that has to show.
        isSimulated: isSimulatedOrigin(tok, t),
        status: normalizeStatus(tokStatus),
        progressPct: progressToPercent(tokProgress),
        order: Number(t.order ?? 0) || 0,
        plannedLength: planned,
        executedLength: produced,
        startedAt: tok?.startedAt ?? t.startedAt ?? null,

        inputs,
        outputs: outId ? [outId] : [],

        jobId,
        groupKey,
        groupHue,
        wireType,
        wireDiameter,
        // fork provenance (drum split into / from another product)
        forkedTo: Array.isArray(t.forkedTo) ? t.forkedTo : null,
        forkedFrom: t.forkedFrom || null,
        statusBadge: statusBadge(tokStatus, tokProgress)
      });
    }
  }
  return rows;
}

// -------------------- tabs (phases) --------------------
function buildPhaseTabsFromTasks(tasks = []) {
  const counts = {};
  for (const t of tasks) {
    const ph = t?.phase;
    if (!ph) continue;
    counts[ph] = (counts[ph] || 0) + 1;
  }

  const tabs = [{ label: 'All', value: 'all', count: tasks.length }];

  for (const phase of PHASES_ORDER) {
    if (!counts[phase]) continue;
    tabs.push({
      label: PHASE_LABEL[phase] || phase,
      value: phase,
      count: counts[phase]
    });
  }

  const known = new Set(PHASES_ORDER);
  const extras = Object.keys(counts).filter(p => !known.has(p));
  extras.sort((a, b) => String(a).localeCompare(String(b)));
  for (const phase of extras) {
    tabs.push({
      label: PHASE_LABEL[phase] || phase,
      value: phase,
      count: counts[phase]
    });
  }

  return tabs;
}

// -------------------- grouping + sorting --------------------
function buildRowsWithGrouping(tasks = [], selectedPhase = 'all') {
  const filtered =
    selectedPhase === 'all'
      ? tasks
      : tasks.filter(t => t.phase === selectedPhase);

  const sorted = [...filtered].sort((a, b) => {
    const ja = String(a.jobId || '');
    const jb = String(b.jobId || '');
    if (ja !== jb) return ja.localeCompare(jb);

    const pa = phaseIndex(a.phase);
    const pb = phaseIndex(b.phase);
    if (pa !== pb) return pa - pb;

    const oa = Number(a.order || 0);
    const ob = Number(b.order || 0);
    if (oa !== ob) return oa - ob;

    return String(a.taskId || '').localeCompare(String(b.taskId || ''));
  });

  let prevKey = null;
  const tmp = sorted.map(t => {
    const groupFirst = t.groupKey !== prevKey;
    prevKey = t.groupKey;
    return { ...t, groupFirst, groupContinue: !groupFirst };
  });

  const groupIndexMap = new Map();
  let gi = 0;
  for (const r of tmp) {
    if (!groupIndexMap.has(r.groupKey)) groupIndexMap.set(r.groupKey, gi++);
    r.groupIndex = groupIndexMap.get(r.groupKey);
  }

  return tmp;
}

// -------------------- find producer/consumer tasks for a token --------------------
function findProducerTaskByTokenId(project, tokenId) {
  const tasksObj = project?.tasks || {};
  for (const t of Object.values(tasksObj)) {
    const outs = Array.isArray(t.outputs) ? t.outputs : [];
    if (outs.includes(tokenId)) return t;
  }
  return null;
}

function findConsumerTasksByTokenId(project, tokenId) {
  const tasksObj = project?.tasks || {};
  const res = [];
  for (const t of Object.values(tasksObj)) {
    const ins = Array.isArray(t.inputs) ? t.inputs : [];
    if (ins.includes(tokenId)) res.push(t);
  }
  return res;
}

// -------------------- Token Details Modal --------------------
const TokenDetailsModal = ({
  show,
  onHide,
  tokenId,
  token,
  producerTask,
  consumerTasks,
  phasePlan,
  log,
  onCorrectLength
}) => {
  const machineId = resolveMachineIdForToken({ producerTask, consumerTasks });
  const { startTime, endTime } = getTokenChartRange(token, log);
  // Whether there is anything real to open. The button used to appear for any
  // drum with a machine, over a window that fell back to the last twelve hours
  // — so a simulated drum drew the machine's real traces from whatever it was
  // actually doing, under this drum's name.
  const trace = machineTraceAvailability({ token, task: producerTask, machineId });
  const navigate = useNavigate();
  const location = useLocation();

  // A route, not a nested modal: the traces used to open fullscreen over the
  // navbar, which dropped you out of the application. Everything the page needs
  // rides in the location state, so it re-fetches nothing and cannot disagree
  // with the screen you came from.
  const openCharts = () =>
    navigate('/apps/project-management/token-charts', {
      state: {
        tokenId,
        token,
        task: producerTask,
        phasePlan,
        machineId,
        startTime,
        endTime,
        // The record travels with it: it holds the corrected window and length,
        // so the page can print the period it is actually drawing.
        log,
        returnTo: location.pathname
      }
    });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
          <span>
            Token{' '}
            {tokenId ? (
              <span style={{ fontFamily: 'monospace' }}>{tokenId}</span>
            ) : null}
          </span>
          {/* The traces used to sit at the bottom of this modal, which was long
              enough that nobody scrolled to them. They open on their own now, at
              the width a trace needs to be read. */}
          {trace.available ? (
            <Button
              size="sm"
              variant="phoenix-primary"
              onClick={openCharts}
              title={`Machine traces for this drum on ${machineId}`}
            >
              Charts
            </Button>
          ) : (
            // Said, not just withheld: a missing button reads as broken, and
            // the reason is the useful part.
            <span className="fs-10 text-700" title={trace.reason}>
              no machine traces
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!token ? (
          <div className="text-600">Token not found in this project.</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: 180 }}>Phase</th>
                    <td>{PHASE_LABEL[token.phase] || token.phase || '—'}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{token.status || '—'}</td>
                  </tr>
                  <tr>
                    <th>Color</th>
                    <td>
                      {token.color ?? '—'}{' '}
                      {token.color ? (
                        <span
                          style={{
                            marginLeft: 8,
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            display: 'inline-block',
                            background: normalizeTokenColor(token.color),
                            border: '1px solid rgba(0,0,0,0.25)'
                          }}
                        />
                      ) : null}
                    </td>
                  </tr>
                  <tr>
                    <th>Planned (m)</th>
                    <td>{Number(token.plannedLength ?? 0).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <th>Produced (m)</th>
                    <td>
                      {Number(token.producedLength ?? 0).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <th>Available (m)</th>
                    <td>
                      {Number(token.availableLength ?? 0).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <th>Consumed (m)</th>
                    <td>
                      {Number(token.consumedLength ?? 0).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <th>Progress</th>
                    <td>{Number(token.progress ?? 0)}%</td>
                  </tr>
                  <tr>
                    <th>Parents</th>
                    <td>
                      {Array.isArray(token.parents) && token.parents.length
                        ? token.parents.join(', ')
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <th>StartedAt</th>
                    <td>
                      {token.startedAt
                        ? new Date(token.startedAt).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="my-4" />

            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: 180 }}>Producer task</th>
                    <td>
                      {producerTask?.taskId ? (
                        <span style={{ fontFamily: 'monospace' }}>
                          {producerTask.taskId}
                        </span>
                      ) : (
                        '—'
                      )}
                      {producerTask?.machineId ? (
                        <span className="text-600 ms-2">
                          (machine: {producerTask.machineId})
                        </span>
                      ) : null}
                    </td>
                  </tr>

                  <tr>
                    <th>Consumer tasks</th>
                    <td>
                      {Array.isArray(consumerTasks) && consumerTasks.length ? (
                        <div className="d-flex flex-column gap-1">
                          {consumerTasks.map(t => (
                            <div key={t.taskId}>
                              <span style={{ fontFamily: 'monospace' }}>
                                {t.taskId}
                              </span>
                              {t.machineId ? (
                                <span className="text-600 ms-2">
                                  (machine: {t.machineId})
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {/* A measurement can be wrong — a meter glitch, a counter reset mid-run.
            Correcting it here re-derives the task, the parent drum's consumption,
            the job material summary and the operator records in one go. */}
        {token && (
          <Button variant="outline-warning" onClick={onCorrectLength} className="me-auto">
            Correct length
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function toFluxTimeFromMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `time(v: "${new Date(n).toISOString()}")`;
}

/**
 * The window to draw the traces over.
 *
 * The RECORD first, the drum second. A drum's `startedAt`/`completedAt` are
 * stamped by the worker as it runs and then never maintained: every repair that
 * moves a boundary — the execution-log edit, a split, the drum chain — writes
 * the corrected window to the ExecutionLog and the ProductionMetric, and
 * `DRUM_FIELDS` (helpers/drumFields.js) does not carry the two timestamps at
 * all. So after correcting a drum's period the card showed the new window and
 * the charts kept drawing the old one, which is the one thing these traces are
 * for: proving what the line was doing while THIS drum was on it.
 *
 * Both are kept rather than one replacing the other, because a drum that has no
 * finished record yet — still running, or produced before execution logging —
 * has a window only on the token.
 */
function getTokenChartRange(token, log) {
  const start =
    toFluxTimeFromMs(log?.startedAt) || toFluxTimeFromMs(token?.startedAt);
  const end =
    toFluxTimeFromMs(log?.completedAt) || toFluxTimeFromMs(token?.completedAt);

  return {
    startTime: start || '-12h',
    endTime: end || 'now()',
    // Which of the two the window came from, so the page can say so instead of
    // leaving the reader to wonder why it does not match the drum card.
    windowFrom: toFluxTimeFromMs(log?.startedAt) ? 'record' : 'drum'
  };
}

function resolveMachineIdForToken({ producerTask, consumerTasks }) {
  if (producerTask?.machineId) return producerTask.machineId;

  if (Array.isArray(consumerTasks) && consumerTasks.length) {
    const firstWithMachine = consumerTasks.find(t => t?.machineId);
    if (firstWithMachine?.machineId) return firstWithMachine.machineId;
  }

  return null;
}

// -------------------- columns factory (needs onTokenClick + tokenMetaById) --------------------
const getProjectTasksColumns = ({
  tokenMetaById,
  onTokenClick,
  estimateByKey = {},
  operatorsByTaskId = {},
  logByTokenId = {},
  onEditLog,
  // Which of this product's drums are one bobbin with another product's.
  sharedByToken = {}
}) => [
  {
    accessorKey: 'taskId',
    header: 'Task',
    cell: ({ row: { original } }) => (
      <span
        style={{ fontFamily: 'monospace' }}
        className="fw-semibold d-inline-flex align-items-center gap-1 flex-wrap"
      >
        {original.taskId}
        {original.forkedFrom?.jobId && (
          <Link
            to={`/apps/project-management/project-details/${original.forkedFrom.jobId}`}
            className="text-decoration-none"
            title={`Split from ${original.forkedFrom.productLabel || original.forkedFrom.jobId}`}
          >
            <Badge bg="info" className="fs-10">
              ⇠ forked from {original.forkedFrom.productLabel || 'source'}
            </Badge>
          </Link>
        )}
        {(original.forkedTo || []).map((f, i) => (
          <Link
            key={i}
            to={`/apps/project-management/project-details/${f.jobId}`}
            className="text-decoration-none"
            title={`Split ${Number(f.length || 0).toLocaleString()} m into ${f.productLabel || f.jobId}`}
          >
            <Badge bg="warning" className="fs-10">
              ⇢ forked to {f.productLabel || 'product'}
            </Badge>
          </Link>
        ))}
      </span>
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">
        {original.tandem && original.tandemPhases?.length
          ? original.tandemPhases
              .map(p => PHASE_LABEL[p] || p)
              .join(' + ')
          : PHASE_LABEL[original.phase] || original.phase || '—'}
        {original.wireDiameter != null && (
          <div className="fs-10 text-700 fw-normal">
            Ø {original.wireDiameter.toFixed(2)} mm
          </div>
        )}
      </span>
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row: { original } }) => {
      const b = original.statusBadge || { label: 'PENDING', type: 'secondary' };
      return (
        <div className="d-flex flex-column justify-content-center align-items-start">
          <Badge bg={b.type} className="text-uppercase">
            {b.label}
          </Badge>
        </div>
      );
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'ps-8'),
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'estimate',
    header: 'Est. Remaining',
    cell: ({ row: { original } }) => (
      <EstimateCell est={estimateByKey[original.rowKey]} />
    ),
    enableSorting: false,
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    id: 'operators',
    header: 'Operator',
    // A phase can be run by several people — a drum handed over mid-run splits
    // between two, and a phase across a shift change routinely has three. The
    // cell names whoever made the most metres and counts the rest; the tooltip
    // has all of them with their share.
    cell: ({ row: { original } }) => {
      const entry = operatorsByTaskId[original.taskId];
      const people = entry?.operators || [];
      if (!people.length) {
        return <span className="text-500">—</span>;
      }
      const title = [
        ...people.map(
          p =>
            `${p.operator} — ${Math.round(p.meters).toLocaleString()} m` +
            (people.length > 1 || entry.unattributed > 0
              ? ` (${Math.round(p.share * 100)}%)`
              : '')
        ),
        // Metres made with nobody signed in at the panel. Said out loud, because
        // otherwise the operator who was there carries the whole drum.
        entry.unattributed > 0
          ? `fără operator — ${Math.round(entry.unattributed).toLocaleString()} m`
          : null
      ]
        .filter(Boolean)
        .join('\n');
      return (
        <span title={title} className="text-nowrap">
          {people[0].operator}
          {people.length > 1 && (
            <Badge bg="secondary" variant="phoenix" className="ms-1">
              +{people.length - 1}
            </Badge>
          )}
        </span>
      );
    },
    enableSorting: false,
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'machineId',
    header: 'Machine',
    // A dash means "we do not know". These two mean "there was no machine", which
    // is a different statement and the one the operator needs.
    cell: ({ row: { original } }) => {
      if (original.machineId) {
        // The machine name stays — it is where the drum was planned to run.
        // The badge says the figures next to it were not measured there.
        return original.isSimulated ? (
          <span className="d-inline-flex align-items-center gap-1 flex-wrap">
            {original.machineId}
            <Badge variant="phoenix" bg="warning" className="fs-10"
              title="Completed with Simulate Execution — the line did not produce this">
              Simulated
            </Badge>
          </span>
        ) : (
          original.machineId
        );
      }
      if (original.isPhantomSource) return 'PHANTOM';
      // A CLAIM on rope another job wound. It has no machine of its own — that
      // is what keeps it off every queue — but the metres WERE wound somewhere,
      // and a drum made on the DTB1250 read as a bare dash (2026-11-05). The
      // donor's line is stamped at settlement under its own name, so this says
      // where they came from without claiming the run was scheduled here.
      if (original.isRopeClaim || original.producedOnMachine) {
        return (
          <span className="d-inline-flex align-items-center gap-1 flex-wrap">
            <Badge variant="phoenix" bg="warning" className="fs-10"
              title="Metres claimed off a drum another lot wound — nothing was scheduled here">
              CLAIMED
            </Badge>
            {original.producedOnMachine && (
              <span className="text-700 fs-10">
                wound on {String(original.producedOnMachine).toUpperCase()}
              </span>
            )}
          </span>
        );
      }
      if (original.isFactorySync) {
        return (
          <Badge variant="phoenix" bg="info" className="fs-10"
            title="Backfilled through Factory Sync — produced before the system recorded it">
            Factory Sync
          </Badge>
        );
      }
      return '—';
    },
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row),
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'Planned (m)',
    cell: ({ row: { original } }) =>
      Number(original.plannedLength || 0).toLocaleString(),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end'),
      headerProps: { style: { width: '10%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'executedLength',
    header: 'Executed (m)',
    cell: ({ row: { original } }) =>
      Number(original.executedLength || 0).toLocaleString(),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end'),
      headerProps: { style: { width: '10%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'progressPct',
    header: 'Progress',
    cell: ({ row: { original } }) => (
      <ProgressCell pct={original.progressPct ?? 0} />
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'text-end'),
      headerProps: { style: { width: '14%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'inputs',
    header: 'Inputs (tokens)',
    cell: ({ row: { original } }) => (
      <TokenChips
        ids={original.inputs}
        tokenMetaById={tokenMetaById}
        onTokenClick={onTokenClick}
      />
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'align-top'),
      headerProps: { style: { width: '15%' } }
    }
  },
  {
    accessorKey: 'outputs',
    header: 'Outputs (tokens)',
    cell: ({ row: { original } }) => (
      <TokenChips
        ids={original.outputs}
        tokenMetaById={tokenMetaById}
        onTokenClick={onTokenClick}
        logByTokenId={logByTokenId}
        onEditLog={onEditLog}
        sharedByToken={sharedByToken}
      />
    ),
    meta: {
      cellProps: ({ row }) => cellPropsForRow(row, 'align-top'),
      headerProps: { style: { width: '15%' } }
    }
  }
];

// -------------------- component --------------------
const ProjectTasksTable = ({ project }) => {
  const tasks = useMemo(() => toTaskRows(project), [project]);

  // Who ran each phase. One request for the whole job; the per-drum logs carry
  // the operator spans, and taskOperators.js folds them up per task.
  const { data: executionLogs } = useJobExecutionLogs(
    project?.jobId || project?._id || project?.id || ''
  );
  const operatorsByTaskId = useMemo(
    () => operatorsByTask(executionLogs?.data || executionLogs || []),
    [executionLogs]
  );
  const tokenMetaById = useMemo(() => project?.tokens || {}, [project]);

  const [selectedPhase, setSelectedPhase] = useState('all');

  // modal state
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [correctingTokenId, setCorrectingTokenId] = useState(null);

  const handleTokenClick = tokenId => {
    setSelectedTokenId(tokenId);
  };

  // The production records of this job's drums, so the Outputs column knows
  // which drums HAVE one. Keyed by token: one row per drum, and a cancelled row
  // is history that has been superseded — editing it would correct a record
  // nothing reads.
  const jobIdForLogs = project?.jobId || project?._id || null;
  const { data: jobLogs } = useQuery({
    queryKey: ['execution-logs', 'job', jobIdForLogs],
    queryFn: () => fetchJobExecutionLogs(jobIdForLogs),
    enabled: !!jobIdForLogs,
    staleTime: 10_000
  });
  const logByTokenId = useMemo(() => {
    const rows = Array.isArray(jobLogs?.data) ? jobLogs.data : [];
    const map = {};
    for (const row of rows) {
      if (!row?.tokenId || row.cancelled) continue;
      map[row.tokenId] = row;
    }
    return map;
  }, [jobLogs]);

  // ── WHICH OF THIS PRODUCT'S DRUMS ARE SHARED, AND WITH WHOM ─────────────
  //
  // A merge execution or a partial fork leaves the SAME tokenId under more than
  // one job, because it is the same object on the floor. This page listed those
  // drums like any other, so a planner reading a product could not tell that a
  // phase of it was run together with somebody else's — *"poti sa pui aceeasi
  // implementare si in cazul in care o parte din acest produs a fost intr-o faza
  // a executiei cu un alt produs, fa la click sa ne duca pe project-detail/:
  // produsul respectiv intr-un tab nou"* (2026-11-03).
  //
  // `byToken` from `/jobs/:id/merge-partners` — the token rule, not
  // `mergeQueueKey`, which marks every drum launched while a phase had Merge
  // Execution on and counts unrelated launches as partners.
  const [sharedByToken, setSharedByToken] = useState({});
  useEffect(() => {
    const id = project?.jobId || project?._id;
    if (!id) return;
    let alive = true;
    http
      .get(`/api/redis/jobs/${id}/merge-partners`)
      .then(res => {
        if (alive) setSharedByToken(res?.data?.byToken || {});
      })
      .catch(() => {
        /* not shared, or unavailable — the chips simply say nothing */
      });
    return () => {
      alive = false;
    };
  }, [project?.jobId, project?._id]);

  // ── THE DRUMS WITH NO RECORD YET, which the pencil used to skip ──────────
  //
  // A record is written when a drum FINISHES, so the pencil appeared on every
  // drum except the ones that can still be changed — and the planned length of
  // a drum still winding is the only thing that decides where the machine stops.
  //
  // Reported 2026-11-02 on a CYYF 3x1.5 of 30 000 planned as two 15 000 m ropes.
  // The first came off at 20 611.92, so the second can only hold ~9 388:
  // *"pe executia ACTIVA nu am icon de edit, ca sa pot modifica planned task si
  // planned token."*
  //
  // `EditExecutionLogModal` opens on a drum alone when `_id` is absent — the
  // DRUM half (`edit-drum`) is the same work, the RECORD half is skipped because
  // there is no row and nothing has been produced to correct.
  const drumEditByTokenId = useMemo(() => {
    const map = { ...logByTokenId };
    const taskOf = tokenId =>
      Object.values(tasks || {}).find(
        t => Array.isArray(t?.outputs) && t.outputs.includes(tokenId)
      ) || null;

    for (const [tokenId, tok] of Object.entries(tokenMetaById || {})) {
      if (!tok || map[tokenId]) continue;
      const task = taskOf(tokenId);
      map[tokenId] = {
        // No `_id`: that is what puts the modal in drum-only mode.
        _id: null,
        jobId: project?.jobId || project?._id || null,
        jobCode: project?.jobCode || null,
        productLabel: project?.productLabel || null,
        tokenId,
        taskId: task?.taskId ?? null,
        phase: tok.phase ?? task?.phase ?? '',
        machineId: task?.machineId ?? null,
        // The five drum fields `edit-drum` writes, read off the drum itself.
        plannedLength: tok.plannedLength ?? null,
        producedLength: tok.producedLength ?? 0,
        color: tok.color ?? null,
        drum_material: tok.drum_material ?? null,
        drum_diameter: tok.drum_diameter ?? null,
        deliveryMode: tok.deliveryMode ?? null,
        carry: tok.carry ?? 0,
        taskPlannedLength: task?.plannedLength ?? null,
        taskExecutedLength: task?.executedLength ?? null
      };
    }
    return map;
  }, [logByTokenId, tokenMetaById, tasks, project]);

  const [editingLog, setEditingLog] = useState(null);

  const closeTokenModal = () => setSelectedTokenId(null);

  const tabItems = useMemo(() => buildPhaseTabsFromTasks(tasks), [tasks]);

  const rows = useMemo(() => {
    return buildRowsWithGrouping(tasks, selectedPhase);
  }, [tasks, selectedPhase]);

  // One estimate request for the whole job — every drum on the machine that will
  // run it. Keyed back onto the rows by rowKey.
  const estimateInput = useMemo(() => projectRowsToEstimateTokens(rows), [rows]);
  const { data: estimateData } = useTokenEstimates(estimateInput);

  const estimateByKey = useMemo(() => {
    const out = {};
    const perToken = estimateData?.data?.perToken || [];
    // perToken preserves the order it was sent in, so map it back positionally —
    // a token id can repeat across phases, rowKey cannot.
    estimateInput.forEach((t, i) => {
      if (perToken[i]) out[t.rowKey] = perToken[i];
    });
    return out;
  }, [estimateData, estimateInput]);

  const estimateTotals = estimateData?.data?.totals || null;

  const columns = useMemo(() => {
    return getProjectTasksColumns({
      tokenMetaById,
      onTokenClick: handleTokenClick,
      estimateByKey,
      operatorsByTaskId,
      // Every drum, not just the ones with a record — see `drumEditByTokenId`.
      logByTokenId: drumEditByTokenId,
      onEditLog: setEditingLog,
      sharedByToken
    });
    // `drumEditByTokenId` is load-bearing, not decorative: the records arrive
    // after the first render, and without it in the deps the columns are never
    // rebuilt — so the pencil would never appear on a drum that has one.
  }, [tokenMetaById, estimateByKey, operatorsByTaskId, drumEditByTokenId, sharedByToken]);

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    setSelectedPhase('all');
  }, [project?.jobId]);

  // selected token + tasks that reference it
  const selectedToken = selectedTokenId
    ? tokenMetaById?.[selectedTokenId]
    : null;
  const producerTask = useMemo(() => {
    if (!selectedTokenId) return null;
    return findProducerTaskByTokenId(project, selectedTokenId);
  }, [project, selectedTokenId]);

  const consumerTasks = useMemo(() => {
    if (!selectedTokenId) return [];
    return findConsumerTasksByTokenId(project, selectedTokenId);
  }, [project, selectedTokenId]);

  return (
    <div>
      <FilterTab
        tabItems={tabItems}
        activeKey={selectedPhase}
        onSelect={setSelectedPhase}
        className="mb-2"
      />

      {estimateTotals && estimateTotals.totalSec > 0 ? (
        <div className="d-flex align-items-center flex-wrap gap-3 mb-2 px-3 py-2 rounded bg-body-tertiary">
          <span className="fs-9 fw-semibold text-900">
            Work remaining: <span className="text-primary">{fmtEta(estimateTotals.totalSec)}</span>
          </span>
          <span className="fs-10 text-700">
            production {fmtEta(estimateTotals.runSec)} · setups {fmtEta(estimateTotals.setupSec)} ·{' '}
            {Math.round(estimateTotals.remainingM).toLocaleString()} m to produce
          </span>
          {/* Said out loud: this is work content, not a promise. Phases wait for
              one another and every machine has a queue in front of it. */}
          <span
            className="fs-10 text-600 fst-italic"
            title="Excludes machine queues, shift boundaries, and phases waiting on one another"
          >
            excludes queues and phase dependencies
          </span>
          {estimateData?.data?.complete === false ? (
            <span className="fs-10 text-warning" title="Measurements are missing — the real time will be longer">
              ⚠ incomplete estimate
            </span>
          ) : null}
        </div>
      ) : null}

      <AdvanceTableProvider {...table}>
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      {!tasks.length && (
        <div className="text-center text-600 py-4">No tasks available.</div>
      )}

      <TokenDetailsModal
        show={!!selectedTokenId && !correctingTokenId}
        onHide={closeTokenModal}
        tokenId={selectedTokenId}
        token={selectedToken}
        producerTask={producerTask}
        consumerTasks={consumerTasks}
        phasePlan={project?.phasePlan}
        log={logByTokenId?.[selectedTokenId] || null}
        onCorrectLength={() => setCorrectingTokenId(selectedTokenId)}
      />

      <EditExecutionLogModal
        show={!!editingLog}
        log={editingLog}
        onHide={() => setEditingLog(null)}
      />

      <CorrectTokenLengthModal
        show={!!correctingTokenId}
        onHide={() => {
          setCorrectingTokenId(null);
          closeTokenModal();
        }}
        jobId={project?.jobId}
        tokenId={correctingTokenId}
        token={correctingTokenId ? tokenMetaById[correctingTokenId] : null}
      />
    </div>
  );
};

function allocationBadge(status) {
  const s = String(status || 'NOT_ALLOCATED').toUpperCase();

  if (s === 'FULLY_ALLOCATED')
    return { label: 'FULLY_ALLOCATED', type: 'success' };
  if (s === 'PARTIALLY_ALLOCATED')
    return { label: 'PARTIALLY_ALLOCATED', type: 'warning' };
  return { label: 'NOT_ALLOCATED', type: 'secondary' };
}

const columns = [
  {
    accessorKey: 'orderCode',
    header: 'Order',
    cell: ({ row: { original } }) => (
      <div>
        <div className="fw-semibold">{original.orderCode}</div>
        <div className="text-600 fs-10" style={{ fontFamily: 'monospace' }}>
          {original.orderId}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'client',
    header: 'Client'
  },
  {
    accessorKey: 'productLabel',
    header: 'Product / Line',
    cell: ({ row: { original } }) => (
      <div>
        <div className="fw-semibold">{original.productLabel}</div>
        <div className="text-600 fs-10" style={{ fontFamily: 'monospace' }}>
          {original.lineId}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'allocatedMeters',
    header: 'Reserved (m)',
    cell: ({ row: { original } }) =>
      Number(original.allocatedMeters || 0).toLocaleString(),
    meta: {
      cellProps: { className: 'text-end' },
      headerProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'remainingLength',
    header: 'Remaining (m)',
    cell: ({ row: { original } }) =>
      Number(original.remainingLength || 0).toLocaleString(),
    meta: {
      cellProps: { className: 'text-end' },
      headerProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'allocationStatus',
    header: 'Reservation',
    cell: ({ row: { original } }) => {
      const b = allocationBadge(original.allocationStatus);
      const units = Number(original.reservedUnits) || 0;
      return (
        <div className="d-flex flex-column align-items-start gap-1">
          <Badge bg={b.type}>{b.label}</Badge>
          {/* The column was called Reservation and showed the allocation
              status — how many METRES this product owes the line. What it had
              actually promised, in drums and coils, was nowhere on the page. */}
          {units > 0 && (
            <span className="fs-10 text-info fw-semibold">
              {units} unit{units === 1 ? '' : 's'} promised
              <span className="text-body-tertiary">
                {' '}
                ({Number(original.reservedMeters || 0).toLocaleString()} m)
              </span>
            </span>
          )}
        </div>
      );
    }
  },
  {
    accessorKey: 'deliveryPlanSummary',
    header: 'To produce',
    cell: ({ row: { original } }) => {
      const items = Array.isArray(original.deliveryPlanItems)
        ? original.deliveryPlanItems
        : [];
      if (!items.length) {
        return (
          <div style={{ whiteSpace: 'normal', minWidth: 240 }}>
            {original.deliveryPlanSummary || '—'}
          </div>
        );
      }
      return (
        <div style={{ whiteSpace: 'normal', minWidth: 240 }} className="lh-sm">
          {items.map((it, i) => {
            const kind = String(it.kind || '').toUpperCase();
            const spec =
              kind === 'DRUM'
                ? [it.drumMaterial, it.drumDiameter ? `Ø${it.drumDiameter}` : '']
                    .filter(Boolean)
                    .join(' ') || 'drum'
                : 'coil';
            return (
              <div key={it.itemId || i}>
                <span className="fw-bold">{it.qtyPlanned}</span> × {spec}{' '}
                {Number(it.lengthPerUnit).toLocaleString()} m
                {/* Promised by THIS product — the rest of the item may be
                    another launch's, or nobody's yet. */}
                {it.reserved > 0 && (
                  <Badge bg="info" variant="phoenix" className="fs-10 ms-2">
                    {it.reserved} promised
                  </Badge>
                )}
                {it.qtyProduced > 0 && (
                  <span className="text-success fs-10 ms-2">
                    {it.qtyProduced} made
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    id: 'release',
    header: '',
    enableSorting: false,
    // A product can serve one order, several, or part of one — so the button
    // belongs on the ROW, which is exactly one allocation. Releasing from the
    // job header would have to ask which; releasing here cannot be ambiguous.
    cell: ({ row: { original } }) => <ReleaseOrderCell row={original} />,
    meta: {
      headerProps: { style: { width: 110 }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'lineTokenIds',
    header: 'Token IDs',
    cell: ({ row: { original } }) => (
      <span style={{ fontFamily: 'monospace' }}>
        {Array.isArray(original.lineTokenIds) && original.lineTokenIds.length
          ? original.lineTokenIds.join(', ')
          : 'Reserved only'}
      </span>
    )
  }
];

/**
 * Give one order back what this product had promised it.
 *
 * The metres return to stock and the reservation on that order's drums and
 * coils is cleared — the product keeps its cable, it loses only the claim.
 *
 * Scoped to the LINE this row is about. A job serving two lines of the same
 * order has two rows, and a button that released both would be doing more than
 * the row it sits on says.
 */
const ReleaseOrderCell = ({ row }) => {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const jobId = row?.jobId;
  const held =
    Number(row?.allocatedMeters || 0) + Number(row?.reservedUnits || 0);

  if (!jobId || held <= 0) return <span className="text-500">—</span>;

  const run = async () => {
    const label = row.orderCode || row.orderId;
    if (
      !window.confirm(
        `Release ${label} from this product?

` +
          `${Number(row.allocatedMeters || 0).toLocaleString()} m go back to stock` +
          (row.reservedUnits
            ? ` and ${row.reservedUnits} promised unit(s) are freed`
            : '') +
          `. The cable is kept — only the claim is dropped.`
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await detachJobFromOrder(jobId, row.orderId, row.lineId);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({
        predicate: q =>
          ['product-allocation', 'token-feasibility'].some(k =>
            String(q.queryKey?.[0] || '').startsWith(k)
          )
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not release');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="phoenix-warning"
        disabled={busy}
        onClick={run}
        title="Return these metres to stock and clear the reservation"
      >
        {busy ? <Spinner animation="border" size="sm" /> : 'Release'}
      </Button>
      {error && <div className="text-danger fs-10 mt-1">{error}</div>}
    </>
  );
};

const ProjectOrdersTable = ({ project }) => {
  const { data, isLoading } = useJobOrdersSummary(project?.jobId);

  // The summary rows describe an allocation but do not name the product they
  // came from — the Release button needs it, and it is right here.
  const rows = useMemo(
    () => (data?.rows || []).map(r => ({ ...r, jobId: project?.jobId })),
    [data, project]
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  if (isLoading) {
    return (
      <div className="text-center text-600 py-4">Loading linked orders...</div>
    );
  }

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      {!rows.length && (
        <div className="text-center text-600 py-4">
          No linked orders for this job.
        </div>
      )}
    </div>
  );
};
